import type { StaticOption } from '@blooq/flow'
import { IonIcon } from '@ionic/react'
import { chevronForwardOutline } from 'ionicons/icons'
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

import type { StepView } from '../lib/api'
import { openMeter, type Meter } from '../lib/level'
import { tap } from '../lib/native'
import { canListen, listen, type Listening } from '../lib/speech'
import { describeDevice, voiceDebug, voiceLines, voiceLog, watchVoiceLog } from '../lib/voicelog'

import { DotSphere } from './DotSphere'
import { OPTION_ICONS } from './steps/ChoiceDock'
import { Composer } from './steps/Composer'

/*
 * The front door, and the open box that follows it into the conversation.
 *
 * A conversation that has not started is not a chat with one bubble in it. It
 * is the business's welcome: a sphere of dots in the business's colour
 * turning in the middle of the page with the six things a customer can do
 * riding on its surface, and the box at the bottom where a thumb already is.
 * No greeting line — the sphere is the welcome. Tap a label on the sphere and
 * that is the choice. Tap the sphere itself and it listens, the dots rippling
 * with the voice and the words appearing above it as they are heard. Type in
 * the box and that is the first message.
 *
 * Whatever the customer does first is the first turn, and from then on the
 * screen is the transcript it always was. The box stays for as long as the
 * step accepts a sentence, and the menu comes back under every reply as
 * chips, so a customer can keep asking or take one of the offers at any
 * point.
 *
 * Nothing here decides what a sentence means or where a label leads. A pick
 * posts its value and a sentence posts `{ text }`; the flow's own `freeText`
 * edge and `industryMenu` routes say where each goes. See
 * packages/flow/src/canonical.ts.
 */

export interface Ask {
  /** What will be sent. While listening, what has been heard so far. */
  text: string
  setText: (text: string) => void
  listening: boolean
  /** Whether a microphone button would do anything on this device. */
  canListen: boolean
  /** Open the microphone, or close it and keep what was heard. */
  toggle: () => void
  submit: () => void
  ready: boolean
  /** 0–1, written on every frame while listening. The sphere reads it. */
  level: MutableRefObject<number>
}

/** The FAQ matcher's own bounds, mirrored so the button greys out honestly. */
const MIN = 3
const MAX = 500

export function useAsk({
  active,
  resetKey,
  onSend,
  onNotice,
}: {
  /** False on a step that takes no sentence; the microphone is released. */
  active: boolean
  /** Changes when a sentence has been sent and answered. The box empties. */
  resetKey: string
  onSend: (text: string) => void
  onNotice: (message: string) => void
}): Ask {
  const [text, setText] = useState('')
  const [listening, setListening] = useState(false)
  /** The recogniser's running sentence. Shown in the box until it commits. */
  const [heard, setHeard] = useState('')

  const session = useRef<Listening | null>(null)
  const meter = useRef<Meter | null>(null)
  const level = useRef(0)
  /** A nudge to the synthetic level each time words arrive, for when there is no meter. */
  const bump = useRef(0)
  const frame = useRef(0)

  const release = useCallback(() => {
    cancelAnimationFrame(frame.current)
    meter.current?.close()
    meter.current = null
    level.current = 0
    bump.current = 0
  }, [])

  /*
   * The level loop, while listening.
   *
   * Reads the meter where there is one. Where there is not — the engine would
   * not share the microphone, or the customer refused the second prompt — it
   * draws a pulse from the words arriving instead, so the sphere still
   * answers the voice even if it cannot hear it.
   */
  useEffect(() => {
    if (!listening) return
    const tick = () => {
      if (meter.current) {
        level.current = meter.current.read()
      } else {
        bump.current *= 0.9
        level.current = bump.current * 0.7 + 0.04 * Math.random()
      }
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame.current)
  }, [listening])

  const stop = useCallback(() => {
    session.current?.stop()
  }, [])

  const start = useCallback(() => {
    voiceLog(`tap: ${describeDevice()}`)
    const started = listen({
      onHeard: (sentence) => {
        bump.current = 1
        setHeard(sentence)
      },
      onFinal: (sentence) => setText(sentence),
      onEnd: (failure, detail) => {
        session.current = null
        setListening(false)
        setHeard('')
        release()
        if (failure === 'refused') {
          onNotice('Microphone access was refused. You can type instead.')
        } else if (failure === 'unavailable') {
          // The recogniser's own code, so a report from a phone says which
          // of its several failures this was.
          onNotice(`Couldn’t hear you just now${detail ? ` (${detail})` : ''}. Try again, or type.`)
        }
      },
    })
    if (!started) {
      onNotice('Dictation isn’t available in this browser. You can type instead.')
      return
    }

    session.current = started
    setHeard('')
    setListening(true)

    /*
     * The level meter opens the microphone a SECOND time, through Web Audio,
     * and on a phone that is the one thing that must not happen: Android
     * Chrome and iOS Safari hand the microphone to the OS recogniser
     * exclusively, and a second capture makes it abort — silently, with no
     * words ever arriving, which is exactly what "voice mode shows no text"
     * looks like. So the meter is desktop-only, where the two coexist. On a
     * phone the sphere pulses from the words as they arrive instead, which
     * is what the level loop above already does without a meter.
     */
    if (window.matchMedia('(pointer: coarse)').matches) {
      voiceLog('meter: skipped on a touch device')
    } else {
      void openMeter().then((opened) => {
        voiceLog(`meter: ${opened ? 'open' : 'unavailable'}`)
        // Listening may have ended while the permission prompt was up.
        if (session.current === started) meter.current = opened
        else opened?.close()
      })
    }
  }, [onNotice, release])

  const toggle = useCallback(() => {
    tap()
    if (session.current) stop()
    else start()
  }, [start, stop])

  /*
   * A sentence answered empties the box.
   *
   * Keyed on the turn count rather than on the step: a send that FAILED — a
   * toast, the same step — leaves the log alone, so the sentence survives
   * exactly when the customer needs it to. Done during render, the way React
   * resets state derived from a prop, so the old sentence is never painted
   * for a frame under the new reply.
   */
  const lastReset = useRef(resetKey)
  if (lastReset.current !== resetKey) {
    lastReset.current = resetKey
    setText('')
  }

  // A step that takes no sentence closes the microphone. Nothing must still
  // be listening on a date picker.
  useEffect(() => {
    if (active) return
    session.current?.cancel()
    session.current = null
    setListening(false)
    setHeard('')
    release()
  }, [active, release])

  useEffect(
    () => () => {
      session.current?.cancel()
      release()
    },
    [release],
  )

  const shown = listening ? heard : text
  const trimmed = text.trim()
  const ready = !listening && trimmed.length >= MIN && trimmed.length <= MAX

  const submit = useCallback(() => {
    if (!ready) return
    tap()
    onSend(trimmed)
  }, [ready, trimmed, onSend])

  return {
    text: shown,
    setText,
    listening,
    canListen: canListen(),
    toggle,
    submit,
    ready,
    level,
  }
}

/**
 * The welcome, in the scroller: the sphere, and a line under it saying what
 * the sphere is for. No greeting — the menu step's prompt is not drawn here,
 * so the first thing on the page is the thing to touch.
 *
 * While the microphone is open the sentence being heard appears above the
 * sphere, growing as it is recognised. That, and the sphere rippling, is the
 * whole feedback for dictation on this screen — so it has to be here and it
 * has to be live.
 */
export function FrontDoorStage({
  view,
  ask,
  busy,
  onPick,
}: {
  view: StepView
  ask: Ask
  busy: boolean
  onPick: (option: StaticOption) => void
}) {
  const heard = ask.listening ? ask.text.trim() : ''
  const mode = busy ? 'thinking' : ask.listening ? 'listening' : 'idle'

  const caption = busy
    ? 'One moment…'
    : ask.listening
      ? 'Listening. Tap the sphere to stop.'
      : ask.canListen
        ? 'Tap the sphere to speak, or drag it to look around.'
        : 'Drag the sphere to look around.'

  return (
    <section className="blooq-door">
      <div className="blooq-door__words">
        {/* Polite, not assertive: a screen reader user dictating does not need
            to be interrupted by every partial word, and the region is read
            when the recogniser pauses. */}
        <p className="blooq-door__heard" aria-live="polite">
          {heard}
        </p>
      </div>

      <div className="blooq-door__stage">
        <DotSphere
          accent={view.tenant.theme?.accent ?? '#0369a1'}
          mode={mode}
          level={ask.level}
          options={view.options}
          onPick={onPick}
          onTap={ask.canListen ? ask.toggle : undefined}
          tapLabel={ask.listening ? 'Stop listening' : 'Speak to us'}
          disabled={busy}
        />
      </div>

      {/* Not a live region: it labels the sphere rather than reporting
          anything, and the sphere's own name already changes with it. */}
      <p className="blooq-door__caption">{caption}</p>

      {voiceDebug() ? <VoiceLog /> : null}
    </section>
  )
}

/**
 * The microphone's log, on screen. Only with `?voice=debug` in the address —
 * see lib/voicelog.ts for why a phone needs this and a laptop does not.
 */
function VoiceLog() {
  const [, bump] = useState(0)
  useEffect(() => watchVoiceLog(() => bump((n) => n + 1)), [])
  const lines = voiceLines()
  return (
    <pre className="blooq-voicelog" aria-label="Microphone log">
      {lines.length === 0
        ? `voice debug on — ${describeDevice()}\n(tap the sphere or the mic and speak)`
        : lines.join('\n')}
    </pre>
  )
}

/**
 * The box, on the front door and on every step after it that takes a sentence.
 *
 * The same composer every other answer goes through, grown to take a sentence
 * and given a microphone. Empty, the round button is the mic; with words in
 * it, the button sends; while listening, it stops.
 */
export function AskBar({
  ask,
  busy,
  error,
  placeholder = 'Type or say what you need',
}: {
  ask: Ask
  busy: boolean
  /** The server's complaint about the last sentence, if any. */
  error?: string
  placeholder?: string
}) {
  return (
    <div className="blooq-walk">
      {error ? <p className="blooq-err">{error}</p> : null}
      <Composer
        multiline
        value={ask.text}
        onChange={ask.setText}
        onSubmit={ask.submit}
        placeholder={ask.listening ? 'Listening…' : placeholder}
        kind="question"
        maxlength={MAX}
        disabled={busy}
        readonly={ask.listening}
        ready={ask.ready}
        mic={ask.canListen ? { listening: ask.listening, onToggle: ask.toggle } : undefined}
      />
    </div>
  )
}

/**
 * The menu as suggestion chips under a reply, each with the icon for its
 * action.
 *
 * The icon is looked up from the option's value — the flow's stable action
 * name — never from its label, so the words can change without the picture
 * going missing. Wrapped, so every offer is in view, and indented to the
 * bubble's edge so it reads as part of the reply.
 */
export function Suggestions({
  options,
  disabled,
  onPick,
}: {
  options: StaticOption[]
  disabled: boolean
  onPick: (option: StaticOption) => void
}) {
  if (options.length === 0) return null

  return (
    <div className="blooq-suggestions" role="group" aria-label="What you can do here">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="blooq-quick"
          disabled={disabled}
          onClick={() => onPick(option)}
        >
          <IonIcon
            className="blooq-quick__icon"
            icon={OPTION_ICONS[option.value] ?? chevronForwardOutline}
            aria-hidden="true"
          />
          {option.label}
        </button>
      ))}
    </div>
  )
}
